import React, { FC, useEffect, useState } from "react";
import { Container } from "../../../components";
import FocusBoxWithLabels from "../../../components/components/molecules/FocusBoxWithLabels";
import { Heading, Text } from "@telia/teddy";
import _filter from "lodash/filter";
import Confetti from "react-dom-confetti";
import { connect, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { DELIVERY_TYPE, DeliveryType, SIM_PICK_UP_IN_STORE_SKU } from "../../../../../constants";
import { RootState } from "../../../../../models";
import { getOrderId } from "../../../app/state/selectors";
import { PhoneNumberFormatters } from "../../../app/utils";
import { fetchPickupPoints } from "../../../pickup-points/state/actions";
import { fetchShopAvailabilityBySku } from "../../../shop-picker/state/actions";
import ContactCard from "../../components/molecules/ContactCard/ContactCard";
import Terms from "../../components/molecules/Terms/Terms";
import CheckoutPage from "../../components/organisms/CheckoutPage/CheckoutPage";
import * as BUSINESS_DETAILS_SELECTORS from "../../state/selectors/businessDetailsSelectors";
import {
    getContactInfoEmail,
    getContactInfoPhone,
    getDeliveryDescription,
    getDeliveryInfoEstimatedDate,
    getDeliveryInfoType,
} from "../../state/selectors/deliveryInfoSelectors";
import TEXT from "../../text-consts-b2b";
import { isDeliveryTypeTracked as deliveryTypeTracked } from "../../utils";
import "../Receipt/Receipt.less";

const CONFETTI_CONFIG = {
    angle: 90,
    spread: 360,
    startVelocity: 40,
    elementCount: 70,
    dragFriction: 0.12,
    duration: 3000,
    stagger: 3,
    width: "10px",
    height: "10px",
    perspective: "800px",
    colors: ["#a864fd", "#29cdff", "#78ff44", "#ff718d", "#fdff6a"],
};

interface StepIndicator {
    hide: boolean;
    labels: string[];
    links: string[];
    steps: number;
}

interface SummaryProps {
    deliveryDescription: string;
    deliveryEstimatedDate: string;
    deliveryInfoContactEmail: string;
    deliveryInfoContactPhone: string;
    orderId?: string;
}

interface StateProps extends SummaryProps {
    contactEmail: string;
    contactName: string;
    contactPhone: string;
    deliveryInfoType?: DeliveryType;
    isDeliveryTypeTracked: boolean;
    organizationAddress: string;
    organizationCity: string;
    organizationName: string;
    organizationPostcode: string;
    stepIndicator: StepIndicator;
}
const ShortSummary: React.FC<SummaryProps> = ({
    deliveryInfoContactEmail,
    orderId,
    deliveryInfoContactPhone,
    deliveryDescription,
    deliveryEstimatedDate,
}) => {
    const deliveryContactInfo = _filter([
        deliveryInfoContactEmail,
        PhoneNumberFormatters[0].displayFn(deliveryInfoContactPhone).trim(),
    ]).join(" / ");

    const content = {
        upperLeft: {
            title: `${TEXT.RECEIPT.ORDER_NUMBER}:`,
            description: orderId,
        },
        bottomRight: {
            title: `${TEXT.RECEIPT.DELIVERY}:`,
            description: deliveryDescription,
        },
        bottomLeft: {
            title: `${TEXT.RECEIPT.DELIVERY_DATE}:`,
            description: deliveryEstimatedDate,
        },
        upperRight: {
            title: `${TEXT.RECEIPT.CONTACT_INFO}:`,
            description: deliveryContactInfo,
        },
    };

    return (
        <Container className="receipt-container__center" size="small">
            <div data-di-mask className="beta-checkout--full-width">
                <FocusBoxWithLabels content={content} />
            </div>
            <div className="receipt-container__credentials-text">
                <Text mb="100">{TEXT.RECEIPT.CREDENTIALS}</Text>
            </div>
        </Container>
    );
};

const B2BReceipt: FC<StateProps> = ({
    deliveryInfoContactEmail,
    deliveryInfoContactPhone,
    deliveryDescription,
    deliveryEstimatedDate,
    deliveryInfoType,
    isDeliveryTypeTracked,
    organizationName,
    organizationCity,
    organizationPostcode,
    organizationAddress,
    contactName,
    contactPhone,
    contactEmail,
    orderId,
    stepIndicator,
}) => {
    const [confettiTrigger, setConfettiTrigger] = useState(false);
    const dispatch = useDispatch();
    useEffect(() => {
        setTimeout(() => setConfettiTrigger(true), 500);
    }, []);

    useEffect(() => {
        switch (deliveryInfoType) {
            case DELIVERY_TYPE.PICK_UP_IN_SHOP:
                dispatch(fetchShopAvailabilityBySku(SIM_PICK_UP_IN_STORE_SKU));
                break;
            case DELIVERY_TYPE.PICK_UP_POINT:
                dispatch(fetchPickupPoints(organizationPostcode));
                break;
        }
    }, [deliveryInfoType, organizationPostcode, fetchPickupPoints, fetchShopAvailabilityBySku]);

    const location = useLocation();

    return (
        <CheckoutPage
            pageHeading={TEXT.RECEIPT.HEADING}
            isFetchingData={false}
            pageIndex={3}
            stepIndicator={stepIndicator}
            path={location.pathname}
        >
            <Container className="receipt-container__section receipt-container__center" size="small" noPaddingTop>
                <Heading mb="100" as="h2" variant="title-200" data-di-mask>
                    {TEXT.RECEIPT.THANKS(organizationName)}
                </Heading>
                <Confetti active={confettiTrigger} config={CONFETTI_CONFIG} />
                <img
                    className="receipt-container__img"
                    src="/shop/assets/img-static/checkout/happy-customer.png"
                    alt=""
                />
                <Text mb="100">
                    {TEXT.RECEIPT.EMAIL_CONFIRMATION} <br />
                    {isDeliveryTypeTracked && TEXT.RECEIPT.TRACKING_SMS}
                </Text>
            </Container>
            <ShortSummary
                deliveryEstimatedDate={deliveryEstimatedDate}
                deliveryDescription={deliveryDescription}
                deliveryInfoContactEmail={deliveryInfoContactEmail}
                deliveryInfoContactPhone={deliveryInfoContactPhone}
                orderId={orderId}
            />
            <ContactCard
                headingText={TEXT.RECEIPT.ORDER_BY}
                name={organizationName}
                address={organizationAddress}
                city={organizationCity}
                contactName={contactName}
                contactPhone={contactPhone}
                contactEmail={contactEmail}
                showContactInfo={true}
            />
            <Container
                id="receipt-container-order"
                className="receipt-container__section receipt-container__shadow"
                size="small"
                whiteBg
                noPaddingTop
            >
                <Heading mb="100" as="h2" variant="title-200">
                    {TEXT.RECEIPT.TERMS_AND_AGREEMENT}
                </Heading>
                <section>
                    <Terms />
                </section>
            </Container>
        </CheckoutPage>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    orderId: getOrderId(state),
    organizationName: BUSINESS_DETAILS_SELECTORS.getOrganizationName(state),
    organizationCity: BUSINESS_DETAILS_SELECTORS.getOrganizationFullCity(state),
    organizationAddress: BUSINESS_DETAILS_SELECTORS.getOrganizationAddress(state),
    organizationPostcode: BUSINESS_DETAILS_SELECTORS.getOrganizationPostcode(state),
    contactName: BUSINESS_DETAILS_SELECTORS.getContactFullName(state),
    contactPhone: BUSINESS_DETAILS_SELECTORS.getContactPhone(state),
    contactEmail: BUSINESS_DETAILS_SELECTORS.getContactEmail(state),
    deliveryInfoContactEmail: getContactInfoEmail(state),
    deliveryInfoContactPhone: getContactInfoPhone(state),
    deliveryDescription: getDeliveryDescription(state),
    deliveryEstimatedDate: getDeliveryInfoEstimatedDate(state),
    isDeliveryTypeTracked: deliveryTypeTracked(getDeliveryInfoType(state)),
    deliveryInfoType: getDeliveryInfoType(state),
    stepIndicator: BUSINESS_DETAILS_SELECTORS.getStepIndicatorData(),
});

export default connect(mapStateToProps)(B2BReceipt);
